import { DetailedHTMLProps, InputHTMLAttributes, useCallback, useState, type ChangeEvent } from 'react';
import ChoiceInput from '../../input/ChoiceInput';
import { NaNToNull } from '../../../utils/NaNToNull';

interface InputProps extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    invalid?: boolean;
}

function Input({ className, invalid = false, ...props }: InputProps) {
    return (
        <input
            {...props}
            className={
                'w-full h-9 round:rounded-lg border px-3 outline-none transition-all caret-current bg-theme-950/70 border-theme-outline placeholder-current placeholder:opacity-40 focus-visible:border-theme-primary focus-visible:ring-2 focus-visible:ring-theme-primary/20' +
                (invalid ? ' border-red-600 bg-red-950/10 focus-visible:ring-red-600/20' : '') +
                (className ? ' ' + className : '')
            }
        />
    );
}

export function TextInput(props: Omit<InputProps, 'type'>) {
    return <Input {...props} type='text' />;
}

export function URLInput(props: Omit<InputProps, 'type'>) {
    return <Input {...props} type='url' />;
}

export function PasswordInput(props: Omit<InputProps, 'type'>) {
    return <Input {...props} type='password' />;
}

interface INumberInput extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'value' | 'onChange'
> {
    disableSpinner?: boolean;
    centered?: boolean;
    invalid?: boolean;
    value?: number | null;
    onChange?: (value: number | null) => void;
}

export function NumberInput({ disableSpinner, centered, invalid = false, onChange, value, ...props }: INumberInput) {
    const [_value, setValue] = useState<number | null>(value ?? null);

    const handleOnChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const value = parseFloat(e.target.value);
            setValue(NaNToNull(value));
            onChange?.(NaNToNull(value));
        },
        [onChange],
    );

    return (
        <input
            {...props}
            type='number'
            value={_value ?? ''}
            onChange={handleOnChange}
            className={
                'w-full h-9 round:rounded-lg border px-3 outline-none transition-all caret-current bg-theme-950/70 border-theme-outline placeholder-current placeholder:opacity-40 focus-visible:border-theme-primary focus-visible:ring-2 focus-visible:ring-theme-primary/20' +
                (disableSpinner ? ' no-spinner' : '') +
                (centered ? ' text-center' : '') +
                (invalid ? ' border-red-600 bg-red-950/10 focus-visible:ring-red-600/20' : '')
            }
        />
    );
}

export function RadioButton(props: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) {
    return <ChoiceInput {...props} type='radio' />;
}
